import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ViewChild, HostListener } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  PipelineStep,
  PipelineDefinition,
  OperationDescriptor,
  ProcessingImage,
  ParamDescriptor,
} from '../../models/processing-pipeline';
import {
  EngineRegistryService,
  PipelineExecutorService,
  PipelineStoreService,
} from '../../../services/processing-pipeline';
import { ProcessService } from '../../../services/process.service';
import { MainFacade } from '../../main.facade';
import { ProcessType, StorageKey } from '../../models/request';
import { Status } from '../../models/status';
import { RequestBuildContext } from '../../models/jit-tool-registry';
import { PipelinePreviewComponent } from './pipeline-preview/pipeline-preview.component';
import { TOOL_BY_OPERATION_ID, OPERATION_TO_PROCESS_TYPE } from '../../models/jit-tool-registry.helpers';
import { CellposeEngine, type ModelDownloadProgress } from '../../models/processing-pipeline/engines/cellpose/cellpose-engine';
import { TransformersJSEngine } from '../../models/processing-pipeline/engines/transformers-js/transformers-engine';
import {
  hasPreviewFeeds,
  type PreviewFeedEngine,
} from '../../models/processing-pipeline/engine.model';

@Component({
  selector: 'app-pipeline-dialog',
  templateUrl: './pipeline-dialog.component.html',
  styleUrls: ['./pipeline-dialog.component.scss'],
})
export class PipelineDialogComponent implements OnInit, OnDestroy {
  @Input() visible = false;
  @Input() inputImage: ProcessingImage | null = null;
  @Input() fullImageSize: { width: number; height: number } | null = null;
  /** Region of the original image the input pixels cover (origin + extent in
   *  full-image pixel coords) when the dialog was opened from a zoomed/cropped
   *  view; null = whole image in view. Drawn regions map back through it. */
  @Input() sourceRect: { x: number; y: number; width: number; height: number } | null = null;
  /** Physical pixel size (µm/px) of the original image, for the preview's scale
   *  bar; scaled to the crop in the preview. null = no physical scale (bar hidden). */
  @Input() sourcePixelSize: { mppX: number | null; mppY: number | null } | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() saveImage = new EventEmitter<Blob>();

  @ViewChild('preview') preview?: PipelinePreviewComponent;

  private readonly destroy$ = new Subject<void>();

  steps: PipelineStep[] = [];
  selectedStepIndex = -1;
  previewImage: ProcessingImage | null = null;
  processing = false;
  cancelling = false;

  /** Model-download progress for the cellpose engine (null when idle). Shows a
   *  progress bar in the preview header instead of the indeterminate spinner. */
  modelDownload: ModelDownloadProgress | null = null;
  /** Human-readable phase string from the cellpose engine, displayed next to
   *  the spinner so the user sees what stage we're in. */
  engineStatus: string | null = null;

  /** Tracks per-step status: 'idle' | 'processing' | 'cached' | 'stale' | 'error' */
  stepStatuses: string[] = [];

  /**
   * The last regions an engine produced, and which step they belong to.
   *
   * Kept rather than consumed. Step results are cached, so re-selecting a step
   * returns its image without re-running the engine — if these were dropped
   * after one use the overlay would vanish on the second look while the viewer
   * kept its copy, which is exactly how it behaved before.
   *
   * One entry per step, not one entry overall. A single record held only the most
   * recent emission, so with two region-producing steps in the pipeline,
   * re-selecting the first served its cached image (emitting nothing) while the
   * record still described the second — `applyPreviewRegions` then rejected it on
   * the step-index check and the first step's overlay simply disappeared.
   *
   * The keys are step indices, so every path that moves or removes a step has to
   * move or remove its entry too — see `movePreviewRegions` and
   * `dropPreviewRegionsAt`. Skipping that would swap a vanishing overlay for a
   * misattributed one, which is worse.
   */
  private previewRegions = new Map<
    number,
    {
      regions: any[];
      /** Which engine produced them, so a re-render replaces only its own. */
      source?: string;
    }
  >();

  // Operation picker
  showOperationPicker = false;
  availableOperations: OperationDescriptor[] = [];
  operationCategories: Map<string, OperationDescriptor[]> = new Map();

  // Presets
  presets: PipelineDefinition[] = [];
  selectedPresetId: string | null = null;
  presetName = '';
  showSavePresetDialog = false;

  // Run on Server (CVAI)
  saveSuffix = '_cvai';
  // Output folder relative to the project bucket. Pre-filled to the input
  // image's parent directory when the dialog opens, so by default results
  // land next to the source instead of in the worker's "output/" fallback.
  // Stamped onto every per-step instruction in runOnServer().
  outputFolder = '';
  runningOnServer = false;
  serverProgress = '';
  showRunOnServerDialog = false;

  // Export/Import
  showImportDialog = false;
  importJson = '';

  constructor(
    private registry: EngineRegistryService,
    private executor: PipelineExecutorService,
    private store: PipelineStoreService,
    private processService: ProcessService,
    private mainFacade: MainFacade,
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.presets = this.store.getAll();

    // Subscribe to cellpose model-download progress so the preview header can
    // render a determinate progress bar instead of the indeterminate spinner
    // for the (one-time) 588 MB CPSAM fetch.
    const cellpose = this.registry.getEngine('cellpose') as CellposeEngine | undefined;
    cellpose?.progress$
      .pipe(takeUntil(this.destroy$))
      .subscribe((p) => { this.modelDownload = p; });
    cellpose?.status$
      .pipe(takeUntil(this.destroy$))
      .subscribe((s) => { this.engineStatus = s; });

    // Same status feed for the transformers.js engine (SAM, depth, segmentation,
    // etc.). Both engines push to the same `engineStatus` field — only one
    // operation runs at a time, so last-write-wins is correct.
    const transformers = this.registry.getEngine('transformers-js') as TransformersJSEngine | undefined;
    transformers?.status$
      .pipe(takeUntil(this.destroy$))
      .subscribe((s) => { this.engineStatus = s; });

    // Same two feeds again for every browser preview engine — YOLO, retinal
    // layer, stain adaptation. Discovered structurally rather than by id: the
    // previous version named 'yolo-seg' explicitly, so each engine added after
    // it shipped inert (no progress, no status, no preview regions, and a null
    // source rect, which silently commits regions at crop-local coordinates).
    for (const engine of this.previewFeedEngines()) {
      engine.progress$
        .pipe(takeUntil(this.destroy$))
        .subscribe((p) => { this.modelDownload = p; });
      engine.status$
        .pipe(takeUntil(this.destroy$))
        .subscribe((s) => { this.engineStatus = s; });
      // Region-producing engines need the crop rect, or their output lands at
      // crop-local coordinates — the wrong place on the viewer.
      engine.setSourceRect?.(this.sourceRect);
      // Regions arrive while the step is still executing, but the preview image
      // is only swapped in afterwards — and setImage resets the viewer's shapes.
      // So hold them and draw once the new image is in place.
      engine.previewRegions$
        ?.pipe(takeUntil(this.destroy$))
        .subscribe((r) => {
          this.previewRegions.set(this.selectedStepIndex, {
            regions: r as any[],
            ...(engine.regionSource ? { source: engine.regionSource } : {}),
          });
        });
    }

    const allOps = this.registry.getAllOperations();
    this.processService
      .getActiveToolNames$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((activeTools: string[]) => {
        if (activeTools.length > 0) {
          this.availableOperations = allOps.filter((op) => {
            if (op.engineId !== 'server') return true;
            const pt = OPERATION_TO_PROCESS_TYPE.get(op.id);
            return pt ? activeTools.includes(String(pt)) : true;
          });
        } else {
          this.availableOperations = allOps;
        }
        this.operationCategories = this.buildOperationCategories(this.availableOperations);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.executor.clearCache();
  }

  private buildOperationCategories(ops: OperationDescriptor[]): Map<string, OperationDescriptor[]> {
    const map = new Map<string, OperationDescriptor[]>();
    for (const op of ops) {
      const list = map.get(op.category) || [];
      list.push(op);
      map.set(op.category, list);
    }
    return map;
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (!this.visible) return;

    // Don't capture when typing in inputs
    const tag = (event.target as HTMLElement)?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA') return;

    switch (event.key) {
      case 'Delete':
      case 'Backspace':
        if (this.selectedStepIndex >= 0) {
          event.preventDefault();
          this.removeStep(this.selectedStepIndex);
        }
        break;
      case ' ':
        if (this.selectedStepIndex >= 0) {
          event.preventDefault();
          this.toggleStep(this.selectedStepIndex);
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (this.selectedStepIndex > 0) {
          this.selectStep(this.selectedStepIndex - 1);
        }
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (this.selectedStepIndex < this.steps.length - 1) {
          this.selectStep(this.selectedStepIndex + 1);
        }
        break;
    }

    // Ctrl/Cmd+S to save result
    if ((event.ctrlKey || event.metaKey) && event.key === 's') {
      event.preventDefault();
      if (this.previewImage && !this.processing) {
        this.saveResult();
      }
    }
  }

  onDialogShow(): void {
    if (this.inputImage && this.steps.length === 0) {
      this.updatePreview(this.inputImage);
    }
  }

  onDialogHide(): void {
    this.visibleChange.emit(false);
  }

  /** Add an operation to the pipeline. */
  addOperation(op: OperationDescriptor): void {
    const defaultParams: Record<string, any> = {};
    for (const p of op.params) {
      defaultParams[p.id] = p.defaultValue;
    }
    this.steps = [
      ...this.steps,
      {
        operationId: op.id,
        engineId: op.engineId,
        params: defaultParams,
        enabled: true,
      },
    ];
    this.stepStatuses.push('idle');
    this.showOperationPicker = false;
    this.selectAddedStep(this.steps.length - 1);
  }

  /**
   * Select a newly added step, executing it only if it is cheap enough to run
   * unasked.
   *
   * `requiresManualRun` exists to stop a multi-second, multi-hundred-megabyte
   * operation from starting on its own — the retinal checkpoint is ~590 MB. But
   * it was consulted only on param change and in the template, so *adding* the
   * operation still called `selectStep` and began that download before the user
   * had chosen a single parameter, which is precisely what the flag documents
   * preventing.
   *
   * The step is marked 'stale' rather than left 'idle' so the param panel's Run
   * button — gated on 'stale' — is live immediately. Nothing is executed, so the
   * preview keeps showing the previous step's output, which is what the user was
   * already looking at.
   */
  private selectAddedStep(index: number): void {
    if (index < 0 || index >= this.steps.length) return;
    if (this.getOperationDescriptor(this.steps[index])?.requiresManualRun) {
      this.selectedStepIndex = index;
      this.stepStatuses[index] = 'stale';
      return;
    }
    void this.selectStep(index);
  }

  /** Remove a step from the pipeline. */
  removeStep(index: number): void {
    this.steps = this.steps.filter((_, i) => i !== index);
    this.stepStatuses.splice(index, 1);
    this.dropPreviewRegionsAt(index);
    this.executor.invalidateFrom(index);
    this.markStaleFrom(index);
    if (this.selectedStepIndex >= this.steps.length) {
      this.selectedStepIndex = this.steps.length - 1;
    }
    if (this.selectedStepIndex >= 0) {
      this.selectStep(this.selectedStepIndex);
    } else {
      this.updatePreview(this.inputImage);
    }
  }

  /** Toggle a step's enabled state. */
  toggleStep(index: number): void {
    this.steps[index] = { ...this.steps[index], enabled: !this.steps[index].enabled };
    this.executor.invalidateFrom(index);
    this.markStaleFrom(index);
    if (this.selectedStepIndex >= index) {
      this.selectStep(this.selectedStepIndex);
    }
  }

  /** Update parameters for a step. */
  updateStepParams(index: number, params: Record<string, any>): void {
    this.steps[index] = { ...this.steps[index], params };
    this.executor.invalidateFrom(index);
    this.markStaleFrom(index);

    // Heavy operations (model inference, server jobs) don't auto-run on param
    // change — every keystroke would trigger a multi-second re-execution.
    // The user must click the explicit "Run" button in the param panel.
    const op = this.getOperationDescriptor(this.steps[index]);
    if (op?.requiresManualRun) return;

    if (this.selectedStepIndex >= index) {
      this.selectStep(this.selectedStepIndex);
    }
  }

  /** Explicit re-run for heavy operations (gated by requiresManualRun). */
  runSelectedStep(): void {
    if (this.selectedStepIndex < 0) return;
    this.selectStep(this.selectedStepIndex);
  }

  /**
   * Select a step and show its output in the preview.
   *
   * ANY call arriving while a run is in flight is dropped, not just a re-entrant
   * one for the same index. This has twelve call sites — adding a step
   * auto-selects it, and the Run button selects it again — so a second call
   * landing while the first is still loading a model was easy to trigger. Both
   * would then reach the engine, and a shared model load means they unblock in
   * the same tick: two concurrent inferences on one ORT session, after which
   * neither finished a tile. The library now queues runs as well, but firing the
   * duplicate at all is pure waste.
   *
   * Guarding only the current index was not enough: the step rows are plain
   * `(click)="selectStep(ri)"` handlers with nothing disabling them, so clicking
   * a *different* row mid-run started a second `executeUpTo` against the same
   * executor. That overwrites the executor's single `_activeEngine` — so Cancel
   * then targets the wrong engine — and lets the two completions call
   * `updatePreview` in either order, leaving the preview showing a step the user
   * is not on.
   *
   * The cost is that a mutation made mid-run (toggle, remove, reorder, param
   * edit) no longer refreshes the preview on its own. That is survivable because
   * every one of those paths calls `markStaleFrom` first, so the affected steps
   * are left 'stale' and the param panel's Run button is live for the re-run.
   */
  async selectStep(index: number): Promise<void> {
    if (!this.inputImage) return;
    if (this.processing) return;
    this.selectedStepIndex = index;
    this.processing = true;

    // Engines must hold the current crop rect *before* they run, not after.
    // `setSourceRect` was reached only from ngOnInit and from the tail of
    // `updatePreview`, and `onDialogShow` refreshes the preview only when there
    // are no steps — so reopening this long-lived dialog over a different crop,
    // with steps already in place, ran inference against the previous open's
    // rectangle and committed regions at those coordinates. `updatePreview` then
    // corrected the rect, too late to matter.
    for (const engine of this.previewFeedEngines()) engine.setSourceRect?.(this.sourceRect);

    // Mark current step as processing
    if (index >= 0 && index < this.stepStatuses.length) {
      this.stepStatuses[index] = 'processing';
    }

    try {
      const result = await this.executor.executeUpTo(this.inputImage, this.steps, index);
      this.updatePreview(result);

      // Mark executed steps as cached
      for (let i = 0; i <= index; i++) {
        if (i < this.stepStatuses.length) {
          this.stepStatuses[i] = 'cached';
        }
      }
    } catch (err: any) {
      if (index >= 0 && index < this.stepStatuses.length) {
        this.stepStatuses[index] = this.cancelling ? 'stale' : 'error';
      }
      // AbortError is user-initiated — don't show an error toast
      if (!this.cancelling) {
        this.messageService.add({ key: 'center-toast',
          severity: 'error',
          summary: 'Processing error',
          detail: err.message || 'An error occurred during processing',
        });
      }
    } finally {
      this.processing = false;
      this.cancelling = false;
    }
  }

  /** Cancel any in-progress long-running operation (e.g. SAM auto-segment). */
  cancelOperation(): void {
    if (!this.processing) return;
    this.cancelling = true;
    this.executor.cancelCurrentOperation();
  }

  /** Drag-and-drop reorder handler. */
  onRowReorder(event: any): void {
    // PrimeNG fires with dragIndex and dropIndex
    const from = event.dragIndex;
    const to = event.dropIndex;
    if (from === to) return;
    this.movePreviewRegions(from, to);
    this.executor.invalidateFrom(Math.min(from, to));
    this.markStaleFrom(Math.min(from, to));
    // Update selected index to follow the moved step
    if (this.selectedStepIndex === from) {
      this.selectedStepIndex = to;
    } else if (from < this.selectedStepIndex && to >= this.selectedStepIndex) {
      this.selectedStepIndex--;
    } else if (from > this.selectedStepIndex && to <= this.selectedStepIndex) {
      this.selectedStepIndex++;
    }
    if (this.selectedStepIndex >= 0) {
      this.selectStep(this.selectedStepIndex);
    }
  }

  /** Move a step up or down (fallback for accessibility). */
  moveStep(index: number, direction: -1 | 1): void {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= this.steps.length) return;
    const temp = this.steps[index];
    this.steps[index] = this.steps[newIndex];
    this.steps[newIndex] = temp;
    this.steps = [...this.steps];
    // Swap statuses too
    const tmpStatus = this.stepStatuses[index];
    this.stepStatuses[index] = this.stepStatuses[newIndex];
    this.stepStatuses[newIndex] = tmpStatus;
    this.movePreviewRegions(index, newIndex);
    this.executor.invalidateFrom(Math.min(index, newIndex));
    this.markStaleFrom(Math.min(index, newIndex));
    this.selectedStepIndex = newIndex;
    this.selectStep(newIndex);
  }

  /** Save the final output image (download). */
  async saveResult(): Promise<void> {
    if (!this.previewImage) return;
    const blob = await this.previewImage.toBlob('image/png');
    this.saveImage.emit(blob);
    this.messageService.add({ key: 'center-toast',
      severity: 'success',
      summary: 'Image saved',
      detail: 'Processed image downloaded successfully',
    });
  }

  /**
   * Open the Run-on-Server dialog. Pre-fills `outputFolder` with the selected
   * file's parent directory so the default is "save next to the input" — the
   * Java side would otherwise fall back to a "output/" folder when the request
   * body omits the field.
   */
  openRunOnServerDialog(): void {
    const fileInfo = this.mainFacade.getSelectedFileInfo();
    const relPath = fileInfo?.relPath || '';
    // Trim the file name to get the parent directory. `relPath` is the storage
    // key relative to the bucket; everything before the last '/' is the parent.
    const slash = relPath.lastIndexOf('/');
    this.outputFolder = slash >= 0 ? relPath.substring(0, slash) : '';
    this.showRunOnServerDialog = true;
  }

  /**
   * Run the current pipeline on the server via the COMPOUND process type.
   * Groups consecutive CVAI operations (opencv/transformers-js) into a single CvAiInstruction,
   * and wraps JIT Server operations as individual instructions.
   * The CompoundRequest orchestrates them sequentially on the full-size image.
   */
  async runOnServer(): Promise<void> {
    const enabledSteps = this.steps.filter((s) => s.enabled && s.engineId !== 'speedy-vision');
    if (enabledSteps.length === 0) {
      this.messageService.add({ key: 'center-toast',
        severity: 'warn',
        summary: 'No operations',
        detail: 'Add at least one enabled operation to run on server.',
      });
      return;
    }

    // Build compound instruction list by grouping consecutive CVAI ops
    const fileInfo = this.mainFacade.getSelectedFileInfo();
    if (!fileInfo || !fileInfo.rawData) {
      this.messageService.add({ key: 'center-toast',
        severity: 'error',
        summary: 'No file selected',
        detail: 'Select a file in the file browser before running on server.',
      });
      return;
    }
    const file = fileInfo.rawData;

    const compoundInstructions: any[] = [];
    let cvaiBuffer: any[] = []; // Buffer for consecutive CVAI ops

    const inputKey = new StorageKey();
    inputKey.bucket = fileInfo.project?.bucket;
    inputKey.object = fileInfo.relPath;

    // Per-instruction outputFolder. Empty string is treated as "let the Java
    // side decide" by some tools; we only stamp it when the user actually
    // provided a path (which the dialog pre-fills with the input's parent).
    const outputFolder = (this.outputFolder || '').trim();

    const flushCvaiBuffer = () => {
      if (cvaiBuffer.length > 0) {
        const req: any = {
          info: 'CVAI',
          inputPaths: [inputKey],
          operations: cvaiBuffer,
          outputFormat: 'png',
          outputSuffix: this.saveSuffix || '_cvai',
        };
        if (outputFolder) req.outputFolder = outputFolder;
        compoundInstructions.push({
          '@typeName': 'org.jax.cimg.api.dao.instr.CvAiInstruction',
          file,
          request: req,
        });
        cvaiBuffer = [];
      }
    };

    for (const step of enabledSteps) {
      if (step.engineId === 'server') {
        // JIT Server operation — flush any pending CVAI ops first, then add the
        // JIT instruction. This branch handles cellpose-sam (Cellpose-SAM/v4) too:
        // its local-preview delegation to the cellpose-js engine is set up via
        // the JIT tool's `previewEngineId` field (consumed by PipelineExecutorService),
        // not via a special case here. On Run-on-Server it's just another JIT
        // instruction (CellPoseSamInstruction → jit-cellpose-sam-worker / CPSAM_QUEUE).
        flushCvaiBuffer();
        const jitInstr = this.buildJitInstruction(step, file);
        if (jitInstr) {
          if (outputFolder && !jitInstr.request.outputFolder) {
            jitInstr.request.outputFolder = outputFolder;
          }
          compoundInstructions.push(jitInstr);
        }
      } else {
        // CVAI operation (opencv or transformers-js) — accumulate
        cvaiBuffer.push({
          engine: step.engineId === 'transformers-js' ? 'transformers' : step.engineId,
          operationId: step.operationId,
          params: this.paramsToStringMap(step.params),
        });
      }
    }
    flushCvaiBuffer();

    // Build the CompoundRequest
    const compoundRequest: any = {
      '@typeName': 'org.jax.cimg.api.dao.instr.CompoundInstruction',
      file,
      request: {
        info: 'COMPOUND',
        requireValidation: false,
        enableFanOut: true,
        requests: compoundInstructions,
      },
    };

    this.runningOnServer = true;
    this.serverProgress = 'Submitting compound pipeline to server...';

    try {
      const response = await firstValueFrom(
        this.processService.run(ProcessType.COMPOUND, compoundRequest as any)
      );
      if (!response || !response.response) {
        throw new Error('Server process submission failed — no workflow URL returned.');
      }

      this.serverProgress = 'Pipeline running on server...';

      // `run()` answers with an absolute status URL, not a bare id — see
      // monitorUrlFor (jit-ui#124).
      const monitorUrl = this.processService.monitorUrlFor(response.response);
      await this.waitForServerCompletion(monitorUrl);

      this.messageService.add({ key: 'center-toast',
        severity: 'success',
        summary: 'Server pipeline complete',
        detail: 'Pipeline sent to server. Check the file Queue UI for status/results.',
      });
      this.showRunOnServerDialog = false;
    } catch (err: any) {
      this.messageService.add({ key: 'center-toast',
        severity: 'error',
        summary: 'Server pipeline failed',
        detail: err.message || 'An error occurred while running the pipeline on server.',
      });
    } finally {
      this.runningOnServer = false;
      this.serverProgress = '';
    }
  }

  /**
   * Build a typed JIT instruction for a server-engine step.
   * Tool metadata (typeName, info, request body) is derived from jit-tool-registry.
   */
  private buildJitInstruction(step: PipelineStep, file: any): any {
    const tool = TOOL_BY_OPERATION_ID.get(step.operationId);
    if (!tool || !tool.javaInstructionTypeName || !tool.buildRequestBody) return null;

    const context: RequestBuildContext = {
      getRegions: () => this.getActiveRegions(),
    };
    const requestBody = tool.buildRequestBody(step.params, context);
    requestBody.info = String(tool.processType);

    return {
      '@typeName': tool.javaInstructionTypeName,
      file,
      request: requestBody,
    };
  }

  /** Regions drawn on the preview, as full-resolution Polygons for the server.
   *  The embedded viewer owns region state and the preview→full-res scaling. */
  private getActiveRegions(): any[] {
    return this.preview?.getRegionPolygons() ?? [];
  }


  /** Check if there are any enabled steps that can run on server (excludes speedy-vision which is client-only). */
  canRunOnServer(): boolean {
    const serverCapable = this.steps.filter((s) => s.enabled && s.engineId !== 'speedy-vision');
    return serverCapable.length > 0 && !this.runningOnServer;
  }

  /** Save current pipeline as a preset. */
  savePreset(): void {
    if (!this.presetName.trim()) return;
    const pipeline: PipelineDefinition = {
      id: this.selectedPresetId || this.store.generateId(),
      name: this.presetName.trim(),
      steps: [...this.steps],
      createdAt: '',
      updatedAt: '',
    };
    this.store.save(pipeline);
    this.presets = this.store.getAll();
    this.selectedPresetId = pipeline.id;
    this.showSavePresetDialog = false;
    this.messageService.add({ key: 'center-toast',
      severity: 'success',
      summary: 'Preset saved',
      detail: `Pipeline "${pipeline.name}" saved`,
    });
  }

  /** Load a preset into the current pipeline. */
  loadPreset(id: string): void {
    const preset = this.store.getById(id);
    if (!preset) return;
    this.steps = [...preset.steps];
    this.stepStatuses = this.steps.map(() => 'idle');
    this.selectedPresetId = preset.id;
    this.presetName = preset.name;
    this.executor.clearCache();
    if (this.steps.length > 0) {
      // Same gate as addOperation: a preset ending in a manual-run operation must
      // not start its download just because the preset was loaded. The cache was
      // just cleared, so in that case the preview stays on the input image until
      // the user clicks Run — a fair trade against an unasked ~590 MB fetch.
      this.selectAddedStep(this.steps.length - 1);
    }
  }

  /** Delete a preset with confirmation. */
  deletePreset(id: string): void {
    const preset = this.store.getById(id);
    this.confirmationService.confirm({
      message: `Delete preset "${preset?.name || id}"?`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.store.delete(id);
        this.presets = this.store.getAll();
        if (this.selectedPresetId === id) {
          this.selectedPresetId = null;
        }
        this.messageService.add({ key: 'center-toast',
          severity: 'info',
          summary: 'Preset deleted',
          detail: `Preset "${preset?.name || id}" deleted`,
        });
      },
    });
  }

  /** Export a preset as a JSON file download. */
  exportPreset(id: string): void {
    const preset = this.store.getById(id);
    if (!preset) return;
    const json = JSON.stringify(preset, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${preset.name.replace(/\s+/g, '_')}.pipeline.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  /** Export all presets as a single JSON file. */
  exportAllPresets(): void {
    const json = JSON.stringify(this.presets, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'pipeline-presets.json';
    a.click();
    URL.revokeObjectURL(a.href);
  }

  /** Import presets from JSON text. */
  importPresets(): void {
    try {
      const parsed = JSON.parse(this.importJson);
      const items = Array.isArray(parsed) ? parsed : [parsed];
      let imported = 0;
      for (const item of items) {
        if (item.steps && Array.isArray(item.steps)) {
          const pipeline: PipelineDefinition = {
            id: item.id || this.store.generateId(),
            name: item.name || `Imported ${++imported}`,
            steps: item.steps,
            createdAt: item.createdAt || '',
            updatedAt: '',
          };
          this.store.save(pipeline);
          imported++;
        }
      }
      this.presets = this.store.getAll();
      this.showImportDialog = false;
      this.importJson = '';
      this.messageService.add({ key: 'center-toast',
        severity: 'success',
        summary: 'Import complete',
        detail: `${imported} preset(s) imported`,
      });
    } catch {
      this.messageService.add({ key: 'center-toast',
        severity: 'error',
        summary: 'Import failed',
        detail: 'Invalid JSON format',
      });
    }
  }

  /** Trigger file input for importing preset JSON. */
  onImportFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const file = input.files[0];
    const reader = new FileReader();
    reader.onload = () => {
      this.importJson = reader.result as string;
      this.importPresets();
    };
    reader.readAsText(file);
    input.value = '';
  }

  /** Get the operation descriptor for a step. */
  getOperationName(step: PipelineStep): string {
    const op = this.availableOperations.find(
      (o) => o.id === step.operationId && o.engineId === step.engineId
    );
    return op?.name || step.operationId;
  }

  /** Get the engine name for a step. */
  getEngineName(step: PipelineStep): string {
    const engine = this.registry.getEngine(step.engineId);
    return engine?.name || step.engineId;
  }

  getEngineLabel(engineId: string): string {
    switch (engineId) {
      case 'opencv': return 'OpenCV';
      case 'speedy-vision': return 'Speedy Vision';
      case 'transformers-js': return 'Transformers';
      case 'server': return 'JIT Server';
      default: return engineId;
    }
  }

  getEngineSeverity(engineId: string): 'info' | 'success' | 'warning' | 'danger' | 'secondary' {
    switch (engineId) {
      case 'opencv': return 'info';
      case 'speedy-vision': return 'success';
      case 'transformers-js': return 'warning';
      case 'server': return 'danger';
      default: return 'secondary';
    }
  }

  getEngineBadgeClass(engineId: string): string {
    return 'p-badge-' + this.getEngineSeverity(engineId);
  }

  getOperationDescriptor(step: PipelineStep): OperationDescriptor | undefined {
    return this.availableOperations.find(
      (o) => o.id === step.operationId && o.engineId === step.engineId
    );
  }

  /** Template helper: update a single param value for the selected step. */
  onParamChange(paramId: string, value: any): void {
    let params = { ...this.steps[this.selectedStepIndex].params, [paramId]: value };

    const tool = TOOL_BY_OPERATION_ID.get(this.steps[this.selectedStepIndex].operationId);
    if (tool?.onParamChange) {
      const updated = tool.onParamChange(paramId, params);
      if (updated) {
        params = updated;
      }
    }

    this.updateStepParams(this.selectedStepIndex, params);
  }

  /** Returns the params that should be visible for the current step, respecting getVisibleParams.
   *  The op's own hook (engine-side) takes precedence; the JIT tool's hook is checked next as a
   *  fallback for server-engine operations registered via jit-tool-registry. */
  getVisibleParams(step: PipelineStep): ParamDescriptor[] {
    const op = this.getOperationDescriptor(step);
    if (!op) return [];
    if (op.getVisibleParams) {
      const visibleIds = op.getVisibleParams(step.params);
      if (visibleIds) {
        return op.params.filter(p => visibleIds.has(p.id));
      }
    }
    const tool = TOOL_BY_OPERATION_ID.get(step.operationId);
    if (tool?.getVisibleParams) {
      const visibleIds = tool.getVisibleParams(step.params);
      if (visibleIds) {
        return op.params.filter(p => visibleIds.has(p.id));
      }
    }
    return op.params;
  }

  /** Returns dynamic options for a select param, or the static options if no filter is defined. */
  getParamOptions(param: any, step: PipelineStep): { label: string; value: any }[] {
    const tool = TOOL_BY_OPERATION_ID.get(step.operationId);
    if (tool?.filterParamOptions) {
      const filtered = tool.filterParamOptions(param.id, step.params);
      if (filtered) return filtered;
    }
    return param.options || [];
  }

  /** Get step count text for preset display. */
  getPresetStepCount(preset: PipelineDefinition): string {
    return `${preset.steps.length} step${preset.steps.length !== 1 ? 's' : ''}`;
  }

  /** Get formatted date for preset display. */
  getPresetDate(preset: PipelineDefinition): string {
    const date = preset.updatedAt || preset.createdAt;
    if (!date) return '';
    try {
      return new Date(date).toLocaleDateString();
    } catch {
      return '';
    }
  }

  /** Get the status icon class for a step. */
  getStepStatusIcon(index: number): string {
    const status = this.stepStatuses[index] || 'idle';
    switch (status) {
      case 'cached': return 'pi pi-check-circle text-green-500';
      case 'processing': return 'pi pi-spin pi-spinner text-blue-500';
      case 'stale': return 'pi pi-refresh text-orange-500';
      case 'error': return 'pi pi-exclamation-circle text-red-500';
      default: return 'pi pi-circle text-400';
    }
  }

  /** Get the status tooltip for a step. */
  getStepStatusTooltip(index: number): string {
    const status = this.stepStatuses[index] || 'idle';
    switch (status) {
      case 'cached': return 'Result cached';
      case 'processing': return 'Processing...';
      case 'stale': return 'Needs re-execution';
      case 'error': return 'Error occurred';
      default: return 'Not yet executed';
    }
  }

  private markStaleFrom(index: number): void {
    for (let i = index; i < this.stepStatuses.length; i++) {
      if (this.stepStatuses[i] === 'cached') {
        this.stepStatuses[i] = 'stale';
      }
    }
  }

  /**
   * Follow a step's regions when the step moves.
   *
   * `previewRegions` is keyed by step index, so a reorder that does not move the
   * keys with it would draw one step's detections while another is selected.
   * Handles both reorder paths: an arbitrary drag-and-drop move, and the
   * accessibility up/down swap (which is just the adjacent case).
   */
  private movePreviewRegions(from: number, to: number): void {
    if (from === to || this.previewRegions.size === 0) return;
    const moved = this.previewRegions.get(from);
    const next = new Map<number, { regions: any[]; source?: string }>();
    for (const [key, value] of this.previewRegions) {
      if (key === from) continue;
      let shifted = key;
      if (from < to && key > from && key <= to) shifted = key - 1;
      else if (from > to && key >= to && key < from) shifted = key + 1;
      next.set(shifted, value);
    }
    if (moved) next.set(to, moved);
    this.previewRegions = next;
  }

  /** Drop a removed step's regions and close the gap in the keys. */
  private dropPreviewRegionsAt(index: number): void {
    if (this.previewRegions.size === 0) return;
    const next = new Map<number, { regions: any[]; source?: string }>();
    for (const [key, value] of this.previewRegions) {
      if (key === index) continue;
      next.set(key > index ? key - 1 : key, value);
    }
    this.previewRegions = next;
  }

  /** Show `image` in the embedded jax-image-visualization viewer (OSD simple-image
   *  mode). Replaces the old bespoke Plotly preview; zoom/pan/region tools now come
   *  from the library's own toolbar. The viewer scales drawn regions back to full
   *  resolution for the server (see PipelinePreviewComponent). */
  /**
   * Draw the current step's engine-produced regions on the preview.
   *
   * Appends rather than assigns: the preview is also where the user draws ROIs
   * for a COMPOUND server run, and replacing wholesale would delete them.
   * Previously injected regions are dropped first so repeated renders do not
   * stack duplicates. Regions belonging to another step are left unapplied.
   */
  private applyPreviewRegions(attempt = 0): void {
    const preview = this.preview;
    const pr = this.previewRegions.get(this.selectedStepIndex);
    if (!preview || !pr) return;

    // Filter by the source marker, not object identity: regions may be re-minted
    // as they pass through the store, which would make an identity check keep
    // stale copies and stack duplicates on every render.
    //
    // Only the *producing* engine's own regions are dropped. Clearing every
    // engine's output would make each run erase the previous engine's results,
    // so a retinal run would wipe a YOLO run on the same preview — and a
    // hardcoded 'yolo' would instead let retinal regions stack on every render.
    const kept = pr.source
      ? preview.getRegions().filter((r: any) => r?.source !== pr.source)
      : preview.getRegions();

    // An empty result is still a result. Returning early here left the previous
    // run's detections on screen, so re-running YOLO or retinal with a stricter
    // threshold and finding nothing looked exactly like finding the old boxes
    // again. Commit the filtered set instead. Without a source marker there is
    // nothing safe to remove, so in that case leave the viewer alone.
    if (pr.regions.length === 0) {
      if (pr.source) preview.setRegions(kept);
      return;
    }

    preview.setRegions([...kept, ...pr.regions]);

    // setImage's promise resolves before the viewer has finished rendering the
    // new image, and that render clears the shapes we just added — verified
    // live: the regions were correct and applied, the viewer held none, and
    // setting the identical regions by hand a moment later worked.
    //
    // There is no "render finished" signal to wait on, so confirm the result
    // and re-apply on a short backoff. Bounded, and it stops as soon as the
    // regions are actually there.
    if (attempt >= 4) return;
    const expected = kept.length + pr.regions.length;
    setTimeout(
      () => {
        if (this.preview !== preview) return;
        if (preview.getRegions().length < expected) this.applyPreviewRegions(attempt + 1);
      },
      120 * (attempt + 1),
    );
  }

  private updatePreview(image: ProcessingImage | null): void {
    this.previewImage = image;
    void this.preview
      ?.setImage(image, this.fullImageSize, this.sourceRect, this.sourcePixelSize)
      .then(() => this.applyPreviewRegions());
    // `sourceRect` is an @Input and the dialog instance outlives a single open,
    // so refresh every engine's copy here rather than only in ngOnInit.
    for (const engine of this.previewFeedEngines()) engine.setSourceRect?.(this.sourceRect);
  }

  /** Every registered engine that publishes the preview feeds. */
  private previewFeedEngines(): PreviewFeedEngine[] {
    return this.registry.getEngines().filter(hasPreviewFeeds);
  }

  /** Convert params to string map (matching CvAiOperation.params on server). */
  private paramsToStringMap(params: Record<string, any>): Record<string, string> {
    const result: Record<string, string> = {};
    for (const [key, value] of Object.entries(params)) {
      result[key] = String(value);
    }
    return result;
  }

  /** Poll the monitor URL until the server process completes or errors. */
  private waitForServerCompletion(monitorUrl: URL): Promise<void> {
    return new Promise((resolve, reject) => {
      this.processService.monitor(monitorUrl, {
        next: (status: Status) => {
          if (status && status.message) {
            this.serverProgress = status.message;
          }
        },
        error: (err) => reject(new Error(`Server pipeline failed: ${err.message || err}`)),
        complete: () => resolve(),
      });
    });
  }
}
